// components/TestRunner/rules/Rule09_PBOGroup_PulseLengthConsistency.js
import { RuleUtils } from "../../../utils/RuleUtils.js";
/**
 * Rule09_PBOGroup_PulseLengthConsistency.js
 *
 * ✅ Règle 09 — Toutes les fibres d'un même PBO doivent être mesurées avec la même longueur d'impulsion
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Groupement des fibres par ropRef.PBO_VALUE
 * • Extraction pulseWidth des métadonnées pvReussite et pvEchec
 * • Validation cohérence : même pulseWidth pour toutes les fibres du groupe PBO
 * • Détection incohérences : valeurs différentes ou données manquantes
 */

export class Rule09_PBOGroup_PulseLengthConsistency {
  /**
   * Appliquer la règle 09 : Cohérence des longueurs d'impulsion PBO
   * @param {Array} allFibers - Toutes les fibres du projet
   */
  static apply(allFibers) {
    // Grouper les fibres par PBO_VALUE
    const pboGroups = this._groupFibersByPBO(allFibers);

    // Analyser chaque groupe pour détecter les incohérences
    Object.entries(pboGroups).forEach(([pboValue, fibers]) => {
      // Skip les groupes avec une seule fibre
      if (fibers.length <= 1) {
        return;
      }

      // Analyser la cohérence du groupe
      const groupAnalysis = this._analyzeGroupConsistency(pboValue, fibers);

      // Si des incohérences sont détectées, ajouter une erreur à chaque fibre du groupe
      if (groupAnalysis.hasInconsistencies) {
        fibers.forEach((fiber) => {
          this._addError(
            fiber,
            "R09",
            `Incohérences de pulseWidth détectées dans le groupe PBO ${pboValue}`,
            this._generateErrorReport(pboValue, groupAnalysis, fibers)
          );
        });
      }
    });
  }

  /**
   * Grouper les fibres par PBO_VALUE
   * @param {Array} allFibers - Toutes les fibres
   * @returns {Object} Groupes de fibres par PBO
   */
  static _groupFibersByPBO(allFibers) {
    const groups = {};

    allFibers.forEach((fiber) => {
      // Vérifier si la fibre a des données SOR (pvReussite ou pvEchec)
      const hasSorData = this._hasSorData(fiber);
      const pboValue = fiber.ropRef?.PBO_VALUE;

      if (hasSorData && pboValue) {
        if (!groups[pboValue]) {
          groups[pboValue] = [];
        }
        groups[pboValue].push(fiber);
      }
    });

    return groups;
  }

  /**
   * Vérifier si une fibre a des données SOR (pvReussite ou pvEchec)
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si des données SOR existent
   */
  static _hasSorData(fiber) {
    const hasReussite = fiber.hasPvReussite && fiber.pvReussite.files.length > 0;
    const hasEchec = fiber.hasPvEchec && fiber.pvEchec.files.length > 0;
    return hasReussite || hasEchec;
  }

  /**
   * Analyser la cohérence d'un groupe de fibres
   * @param {string} pboValue - Valeur PBO du groupe
   * @param {Array} fibers - Fibres du groupe
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzeGroupConsistency(pboValue, fibers) {
    const analysis = {
      pboValue: pboValue,
      fiberCount: fibers.length,
      hasInconsistencies: false,
      issues: [],
    };

    // Extraire les données SOR de chaque fibre
    const fiberData = fibers.map((fiber) => this._extractSorData(fiber));

    // Analyser la cohérence des pulseWidth
    this._analyzePulseWidthConsistency(analysis, fiberData);

    return analysis;
  }

  /**
   * Extraire les données SOR d'une fibre
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Données SOR extraites
   */
  static _extractSorData(fiber) {
    const pulseWidths = [];
    let type = "";
    let hasData = false;

    // Extraire de pvReussite
    if (fiber.hasPvReussite && fiber.pvReussite && fiber.pvReussite.files && fiber.pvReussite.files.length > 0) {
      type = "Réussite";
      hasData = true;
      fiber.pvReussite.files.forEach((file) => {
        if (file.metadata && file.metadata.pulseWidth !== undefined) {
          pulseWidths.push(file.metadata.pulseWidth);
        }
      });
    }

    // Extraire de pvEchec si pas de pvReussite
    else if (fiber.hasPvEchec && fiber.pvEchec && fiber.pvEchec.files && fiber.pvEchec.files.length > 0) {
      type = "Échec";
      hasData = true;
      fiber.pvEchec.files.forEach((file) => {
        if (file.metadata && file.metadata.pulseWidth !== undefined) {
          pulseWidths.push(file.metadata.pulseWidth);
        }
      });
    }

    return {
      fiberId: fiber.id,
      type: type,
      pulseWidths: pulseWidths,
      hasData: hasData,
    };
  }

  /**
   * Analyser la cohérence des pulseWidth
   * @param {Object} analysis - Objet d'analyse à modifier
   * @param {Array} fiberData - Données des fibres
   */
  static _analyzePulseWidthConsistency(analysis, fiberData) {
    // Vérifier les données manquantes
    const missingData = fiberData.filter((data) => !data.hasData || data.pulseWidths.length === 0);

    if (missingData.length > 0) {
      analysis.hasInconsistencies = true;
      analysis.issues.push({
        type: "missing_pulse_width",
        fibers: missingData.map((fiber) => ({
          fiberId: fiber.fiberId,
          type: fiber.type,
        })),
      });
    }

    // Pooler toutes les valeurs pulseWidth de toutes les fibres
    const allPulseWidths = [];
    const validData = fiberData.filter((data) => data.hasData && data.pulseWidths.length > 0);
    validData.forEach((data) => {
      allPulseWidths.push(...data.pulseWidths);
    });

    if (allPulseWidths.length > 1) {
      const uniquePulseWidths = [...new Set(allPulseWidths)];

      if (uniquePulseWidths.length > 1) {
        analysis.hasInconsistencies = true;
        analysis.issues.push({
          type: "pulse_width_mismatch",
          uniqueValues: uniquePulseWidths,
          fiberDetails: validData.map((data) => ({
            fiberId: data.fiberId,
            type: data.type,
            pulseWidths: data.pulseWidths,
          })),
        });
      }
    }
  }

  /**
   * Générer le rapport d'erreur
   * @param {string} pboValue - Valeur PBO du groupe
   * @param {Object} groupAnalysis - Résultat de l'analyse
   * @param {Array} fibers - Fibres du groupe
   * @returns {string} Rapport HTML formaté
   */
  static _generateErrorReport(pboValue, groupAnalysis, fibers) {
    let report = `<strong>⚠️ Problème détecté :</strong><br>`;
    report += `Incohérences de pulseWidth détectées dans le groupe PBO ${pboValue}<br><br>`;

    report += `<strong>Détail du problème :</strong><br>`;
    report += `- Fibres dans le groupe: ${groupAnalysis.fiberCount}<br>`;
    report += `- Problèmes détectés: ${groupAnalysis.issues.length}<br><br>`;

    report += `<strong>Fibres du groupe :</strong><br>`;
    fibers.forEach((fiber) => {
      const type = fiber.pvReussite && fiber.pvReussite.length > 0 ? "Réussite" : "Échec";
      report += `• ${RuleUtils.formatFiberId(fiber.id)} (${type})<br>`;
    });
    report += `<br>`;

    groupAnalysis.issues.forEach((issue) => {
      if (issue.type === "missing_pulse_width") {
        report += `<strong>Données manquantes:</strong> PulseWidth non trouvé dans les métadonnées<br>`;
        issue.fibers.forEach((fiber) => {
          report += `&nbsp;&nbsp;• ${fiber.fiberId} (${fiber.type}): aucune valeur pulseWidth<br>`;
        });
      } else if (issue.type === "pulse_width_mismatch") {
        report += `<strong>Valeurs incohérentes:</strong> Différentes valeurs de pulseWidth détectées<br>`;
        report += `&nbsp;&nbsp;• Valeurs trouvées: ${issue.uniqueValues.join(", ")}<br>`;
        report += `&nbsp;&nbsp;• Détail par fibre:<br>`;
        issue.fiberDetails.forEach((detail) => {
          report += `&nbsp;&nbsp;&nbsp;&nbsp;- ${detail.fiberId} (${detail.type}): ${detail.pulseWidths.join(", ")}<br>`;
        });
      }
      report += `<br>`;
    });

    report += `<strong>⚡ Action requise :</strong><br>`;
    report += `- Vérifier les paramètres de mesure des fibres de ce groupe PBO<br>`;
    report += `- S'assurer que toutes les fibres ont été mesurées avec le même pulseWidth<br>`;
    report += `- Reprendre les mesures avec des paramètres cohérents si nécessaire`;

    return report;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
